import React, { useCallback, useEffect, useState } from "react";
import { Form, Input, Select, Upload, Button, notification } from "antd";
import Icon, { DeleteOutlined, FileOutlined } from "@ant-design/icons";
import { httpService } from "../../base/services/httpService.service";
import AuthService from "../../base/services/authentication.service";
import { StatusCodesEnum } from "../../utils/enums";
import BasicModal from "../../ui/BasicModal/BasicModal";

import "./Tickets.scss";

const { TextArea } = Input;

const AddNewTicket = React.memo(({ isModalOpen, handleModal, fetchData }) => {
   const [form] = Form.useForm();
   const [isCreating, setIsCreating] = useState(false);
   const [fileList, setFileList] = useState([]);
   const [previewImages, setPreviewImages] = useState([]);
   const [ticketTypes, setTicketTypes] = useState([]);
   const user = AuthService?.getUser();

   const fetchDataTypes = useCallback(() => {
      try {
         httpService.get(
            "/Tickets/getTicketTypes",
            (response) => {
               const data = Object.values(response.data).map((item) => ({
                  value: item,
                  label: item
               }));
               setTicketTypes(data);
            },
            (_err) => {}
         );
      } catch (error) {
         console.error("Error fetching tickets data", error);
      }
   }, []);

   const getBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const fileToRead = file.originFileObj || file;
         if (fileToRead instanceof Blob) {
            const reader = new FileReader();
            reader.readAsDataURL(fileToRead);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
         } else {
            reject(new Error("Invalid file format"));
         }
      });
   };

   const getPreviewImage = async (file) => {
      return await getBase64(file);
   };

   useEffect(() => {
      fetchDataTypes();

      const loadImages = async () => {
         const previews = await Promise.all(
            fileList.map(async (file) => ({
               id: file,
               url: await getPreviewImage(file)
            }))
         );
         setPreviewImages(previews);
      };

      if (fileList?.length) {
         loadImages();
      } else {
         setPreviewImages([]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fileList, fetchDataTypes]);

   const handleChange = ({ fileList }) => {
      setFileList(fileList.slice(0, 3));
      const attachmentIds = fileList.slice(0, 3).map((file) => file.originFileObj);
      form.setFieldValue("attachments", attachmentIds);
   };

   const deleteUpload = (el) => {
      if (isCreating) return;
      const newData = fileList?.filter((e) => e.uid !== el?.uid);
      const attachmentIds = newData.map((file) => file.originFileObj);
      form.setFieldValue("attachments", attachmentIds);
      setFileList(newData);
   };

   const handleSubmit = async (values) => {
      setIsCreating(true);
      const formData = new FormData();
      formData.append("Type", values.type);
      formData.append("Subject", values.summary);
      formData.append("LastResponse", values.description);

      (values.attachments || []).forEach((file) => {
         formData.append("Attachments", file);
      });

      try {
         const response = await httpService.post("/Tickets/createTicket", formData);
         const { statusCode, data } = response || {};

         if (statusCode === StatusCodesEnum.OK) {
            notification.success({
               message: data?.message || "Ticket created successfully!"
            });
            fetchData();
            handleModal();
         } else {
            notification.warning({
               message: data?.message || "Something went wrong! Please try again later."
            });
         }
         setIsCreating(false);
      } catch (error) {
         notification.error({
            message:
               error?.response?.data?.message || "Something went wrong. Please try again later."
         });
         setIsCreating(false);
      }
   };

   return (
      <BasicModal
         isModalOpen={isModalOpen}
         handleCancel={handleModal}
         width={600}
         footerButtonCustomStyle
      >
         <Form
            form={form}
            className="ticket-form"
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
         >
            <h2 className="ticket-form__title">Add ticket</h2>

            <Form.Item label="Email" name="email">
               <Input defaultValue={user?.email} disabled />
            </Form.Item>

            <Form.Item
               label="Ticket type"
               name="type"
               rules={[{ required: true, message: "Please select a ticket type" }]}
            >
               <Select allowClear placeholder="Select type" options={ticketTypes} />
            </Form.Item>

            <Form.Item
               label="Summary"
               name="summary"
               rules={[{ required: true, message: "Please enter a summary" }]}
            >
               <Input placeholder="Enter a name" />
            </Form.Item>

            <Form.Item
               label="Describe the problem in detail"
               name="description"
               rules={[{ required: true, message: "Please describe the problem" }]}
            >
               <TextArea />
            </Form.Item>

            <Form.Item label="Attach any evidence you consider important" name="attachments">
               <div className="ticket-form__images">
                  {previewImages.map(({ id, url }) => {
                     const isImage = id.type.includes("image");

                     return (
                        <div className="ticket-form__images-item" key={id.uid}>
                           <div
                              className="ticket-form__images-close"
                              onClick={() => deleteUpload(id)}
                           >
                              <DeleteOutlined />
                           </div>

                           {isImage ? (
                              <img
                                 className="ticket-form__images-item-img"
                                 src={url}
                                 alt="Preview"
                              />
                           ) : (
                              <div className="ticket-form__file-icon">
                                 <FileOutlined />
                              </div>
                           )}
                        </div>
                     );
                  })}
               </div>

               <Upload.Dragger
                  name="files"
                  listType="picture-card"
                  multiple
                  accept=".png,.jpeg,.jpg,.gif,.webp, .xls,.xlsx, .docx, .pdf"
                  showUploadList={false}
                  disabled={isCreating}
                  fileList={fileList}
                  beforeUpload={(file) => {
                     if (fileList.length < 3) {
                        setFileList([...fileList, file]);
                     }
                     return false;
                  }}
                  className="ticket-form__uploader"
                  onChange={handleChange}
               >
                  <div className="ticket-form__uploader-icon">
                     <Icon
                        component={() => (
                           <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                           >
                              <path
                                 d="M4.32084 17.2432H1.88494C1.56192 17.2432 1.25213 17.1088 1.02372 16.8697C0.795311 16.6305 0.666992 16.3062 0.666992 15.968V1.94174C0.666992 1.60356 0.795311 1.27923 1.02372 1.0401C1.25213 0.800968 1.56192 0.666626 1.88494 0.666626H15.2824C15.6054 0.666626 15.9152 0.800968 16.1436 1.0401C16.372 1.27923 16.5003 1.60356 16.5003 1.94174V15.968C16.5003 16.3062 16.372 16.6305 16.1436 16.8697C15.9152 17.1088 15.6054 17.2432 15.2824 17.2432H12.8465M0.666992 4.49228H16.5003M8.58311 17.2421V8.9538M8.58311 8.9538L5.53911 12.1416M8.58311 8.9538L11.6288 12.1416"
                                 stroke="#4158CF"
                                 strokeWidth="1.25"
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                              />
                           </svg>
                        )}
                     />
                  </div>
                  <p className="ticket-form__uploader-text">
                     Drag and drop files here, or <span>click to select files</span>
                  </p>
                  <p className="ticket-form__uploader-hint">JPG, PNG, GIF and WEBP</p>
                  <p className="ticket-form__uploader-subtext">Max file size: 20MB</p>
               </Upload.Dragger>
            </Form.Item>

            <div className="ticket-form__actions">
               <Button onClick={handleModal} className="defaultButton">
                  Cancel
               </Button>
               <Button
                  type="primary"
                  htmlType="submit"
                  className="kapSurveyPrimaryButton"
                  loading={isCreating}
               >
                  Create ticket
               </Button>
            </div>
         </Form>
      </BasicModal>
   );
});

export default AddNewTicket;
